import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AlertMiscFieldValue } from '../../../../models/api/alerts/alert-condition-code';
import { AlertCustomField } from '../../../../models/api/alerts/alert-custom-field';
import { AlertsService } from '../../../../services/alerts/alerts.service';
import { Dispose } from '../../../../utilities/dispose';
import { InputComponent } from '../../../core/controls/input.component';
import { RadioButtonComponent } from '../../../core/controls/radio-button.component';
import { RangeComponent } from '../../../core/controls/range.component';
import { SelectComponent } from '../../../core/controls/select.component';
import { SelectOptionComponent } from './select-option.component';

interface AlertMiscFieldOption {
  value: number;
  description: string;
  dataType?: string;
  hasLookup: boolean;
  format?: string;
}

@Component({
  selector: 'app-custom-field-option',
  imports: [
    SelectComponent,
    InputComponent,
    RadioButtonComponent,
    SelectOptionComponent,
    RangeComponent,
  ],
  template: ` <div class="tw-grow">
      <app-select-option
        label="Custom field"
        [control]="fg.controls.fieldName"
        [items]="clientFields"
        optionValue="value"
        [multiple]="false"
        [filter]="true"
        [virtualScroll]="false"
        selectLabel="Name"
      />
    </div>
    @switch (getFieldType(fg.controls.fieldName.value)) {
      @case ('character') {
        @if (getValueList(fg.controls.fieldName.value).length > 0) {
          <div
            class="tw-col-span-2 tw-flex"
            role="group"
            aria-labelledby="cf-char-list-label"
          >
            <span
              class="label tw-grow tw-font-bold tw-pt-2"
              id="cf-char-list-label"
              >Value is</span
            >
            <app-select
              [control]="fg.controls.operatorList"
              label="Conditions"
              optionLabel="label"
              optionValue="value"
              [options]="optionsList"
              class="tw-w-[200px]"
              [showClear]="false"
              [filter]="false"
              [virtualScroll]="false"
              [showHeader]="false"
            ></app-select>
            <app-select
              [control]="fg.controls.valueSelectedCharacter"
              label="Values"
              optionLabel="description"
              optionValue="value"
              [options]="getValueList(fg.controls.fieldName.value)"
              class="tw-w-[300px]"
              [multiple]="true"
              [virtualScroll]="false"
            ></app-select>
          </div>
        } @else {
          <div
            class="tw-col-span-2 tw-flex tw-flex-col tw-gap-1"
            role="group"
            aria-labelledby="cf-char-nolist-label"
          >
            <div class="tw-flex">
              <span
                class="label tw-grow tw-font-bold tw-pt-2"
                id="cf-char-nolist-label"
                >Value is</span
              >
              <app-select
                [control]="fg.controls.operatorNoDescription"
                label="Conditions *"
                optionLabel="label"
                optionValue="value"
                [options]="optionsNoValueDescription"
                class="tw-w-[200px]"
                [showClear]="false"
                [filter]="false"
                [virtualScroll]="false"
                [showHeader]="false"
              ></app-select>
              <app-input
                [control]="fg.controls.valueCharacter"
                label="Values"
                class="tw-w-[300px]"
                [maxlength]="getMaxLength(fg.controls.fieldName.value)"
              />
            </div>
            @if (getMaxLength(fg.controls.fieldName.value) > 0) {
              <span
                class="tw-text-sm tw-text-[var(--sed-black-60-color)] tw-pl-2"
              >
                {{ (fg.controls.valueCharacter.value || '').length }} /
                {{ getMaxLength(fg.controls.fieldName.value) }}
              </span>
            }
          </div>
        }
      }
      @case ('integer') {
        <div
          class="tw-col-span-2 tw-flex"
          role="group"
          aria-labelledby="cf-integer-label"
        >
          <span class="label tw-grow tw-font-bold tw-pt-2" id="cf-integer-label"
            >Value is</span
          >
          <app-select
            [control]="fg.controls.operatorInteger"
            label="Conditions"
            optionLabel="label"
            optionValue="value"
            [options]="optionsInteger"
            class="tw-pt-1.5 tw-w-[250px]"
            [showClear]="true"
            [filter]="false"
            [virtualScroll]="false"
            [showHeader]="false"
          ></app-select>
          @if (fg.controls.operatorInteger.value === 'between') {
            <app-range
              [control]="fg.controls.valueIntegerFrom"
              [toControl]="fg.controls.valueIntegerTo"
              label=""
              type="Integer"
              class="tw-w-[300px]"
            />
          } @else {
            <app-input
              [control]="fg.controls.valueInteger"
              label="Value"
              type="Integer"
              class="tw-pt-1.5 tw-w-[150px]"
            />
            <app-input
              [control]="fg.controls.empty"
              label=""
              class="tw-pt-1.5 tw-w-[150px]"
            />
          }
        </div>
      }
      @case ('decimal') {
        <div
          class="tw-col-span-2 tw-flex"
          role="group"
          aria-labelledby="cf-decimal-label"
        >
          <span class="label tw-grow tw-font-bold tw-pt-2" id="cf-decimal-label"
            >Value is</span
          >
          <app-select
            [control]="fg.controls.operatorInteger"
            label="Conditions"
            optionLabel="label"
            optionValue="value"
            [options]="optionsInteger"
            class="tw-pt-1.5 tw-w-[250px]"
            [showClear]="true"
            [filter]="false"
            [virtualScroll]="false"
            [showHeader]="false"
          ></app-select>
          @if (fg.controls.operatorInteger.value === 'between') {
            <app-range
              [control]="fg.controls.valueDecimalFrom"
              [toControl]="fg.controls.valueDecimalTo"
              label=""
              type="Decimal"
              class="tw-w-[300px]"
            />
          } @else {
            <app-input
              [control]="fg.controls.valueDecimal"
              label="Value"
              type="Decimal"
              class="tw-pt-1.5 tw-w-[150px]"
            />
            <app-input
              [control]="fg.controls.empty"
              label=""
              class="tw-pt-1.5 tw-w-[150px]"
            />
          }
        </div>
      }
      @case ('logical') {
        <div
          class="tw-col-span-2 tw-flex"
          role="group"
          aria-labelledby="cf-logical-label"
        >
          <span class="label tw-grow tw-font-bold tw-pt-2" id="cf-logical-label"
            >Value is
          </span>
          <app-radio-button
            [control]="fg.controls.valueBoolean"
            optionLabel="label"
            optionValue="value"
            [options]="booleanOptions"
            [vertical]="false"
            class="tw-w-[200px] tw-pt-1"
          ></app-radio-button>
        </div>
      }
      @case ('text') {
        <div
          class="tw-col-span-2 tw-flex"
          role="group"
          aria-labelledby="cf-text-label"
        >
          <span class="label tw-grow tw-font-bold tw-pt-2" id="cf-text-label"
            >Value is</span
          >
          <app-input
            [control]="fg.controls.valueText"
            label="Values"
            class="tw-w-[300px]"
          />
        </div>
      }
      @case ('date') {
        <div
          class="tw-col-span-2 tw-flex"
          role="group"
          aria-labelledby="cf-date-gte-label"
        >
          <span
            class="label tw-grow tw-font-bold tw-pt-2"
            id="cf-date-gte-label"
            >Is greater than or equal to</span
          >
          <app-input
            [control]="fg.controls.valueDateDays"
            label="Days"
            type="Integer"
            class="tw-w-[100px]"
          />
        </div>
        <div
          class="tw-col-span-2 tw-flex"
          role="group"
          aria-labelledby="cf-date-notify-label"
        >
          <span
            class="label tw-grow tw-font-bold tw-pt-2"
            id="cf-date-notify-label"
            >Send notifications for</span
          >
          <app-select
            [control]="fg.controls.valueNotifyDays"
            label=""
            optionLabel="label"
            optionValue="value"
            [options]="notifyOptions"
            class="tw-w-[100px]"
            [showClear]="false"
            [filter]="false"
            [virtualScroll]="false"
            [showHeader]="false"
            ariaLabelledBy="cf-date-notify-label"
          ></app-select>
        </div>
      }
    }`,
  styles: ``,
})
export class CustomFieldOptionComponent implements OnInit, OnDestroy {
  private alerts = inject(AlertsService);

  @Input()
  control: FormControl<AlertCustomField>;

  dispose = new Dispose();

  fg = new FormGroup({
    fieldName: new FormControl<number>(null),
    operatorList: new FormControl<string>(null),
    operatorNoDescription: new FormControl<string>(null),
    operatorInteger: new FormControl<string>(null),
    valueDateDays: new FormControl<number>(null),
    valueNotifyDays: new FormControl<number>(1),
    valueInteger: new FormControl<number>(null),
    valueDecimal: new FormControl<number>(null),
    valueBoolean: new FormControl<boolean>(null),
    valueSelectedCharacter: new FormControl<string[]>(null),
    valueCharacter: new FormControl<string>(null),
    valueText: new FormControl<string>(null),
    valueIntegerFrom: new FormControl<number>(null),
    valueIntegerTo: new FormControl<number>(null),
    valueDecimalFrom: new FormControl<number>(null),
    valueDecimalTo: new FormControl<number>(null),

    empty: new FormControl<string>({ value: '', disabled: true }),
  });

  async ngOnInit() {
    this.addValidatorsAndDefaults();
    this.control.addValidators(Validators.required);
    this.control.updateValueAndValidity({ emitEvent: false });

    const alert = this.alerts.currentAlert();
    const contractNumber = alert?.contracts?.[0]?.contNum ?? null;
    const lobs = alert?.linesOfBusiness ?? [];

    await this.loadFields(contractNumber, lobs);

    if (this.control?.value) {
      this.applyControl(this.control.value);
    }

    this.dispose.subs = this.control?.valueChanges?.subscribe((value) => {
      if (value && !this.fg.controls.fieldName.value) {
        this.applyControl(value);
        this.onFieldChanged(value.fieldNumber);
      }
    });

    this.dispose.subs = this.fg.controls.fieldName.valueChanges.subscribe(
      (fieldNumber) => {
        this.onFieldChanged(fieldNumber);
      },
    );

    this.dispose.subs = this.fg.valueChanges.subscribe(() => {
      this.updateControl();
    });

    this.dispose.subs = this.fg.controls.operatorInteger.valueChanges.subscribe(
      (value) => {
        this.updateNumericValidators(value);
      },
    );

    this.dispose.subs =
      this.fg.controls.operatorNoDescription.valueChanges.subscribe((value) => {
        this.updateCharacterValueState(value);
      });

    this.onFieldChanged(this.fg.controls.fieldName.value);
    this.updateControl();
  }

  ngOnDestroy(): void {
    this.dispose.dispose();
  }

  addValidatorsAndDefaults() {
    this.fg.controls.fieldName.addValidators([Validators.required]);
    this.fg.controls.operatorList.addValidators([Validators.required]);
    this.fg.controls.operatorNoDescription.addValidators([Validators.required]);
    this.fg.controls.valueSelectedCharacter.addValidators([
      Validators.required,
    ]);
    this.fg.controls.valueText.addValidators([Validators.required]);
    this.fg.controls.valueCharacter.addValidators([Validators.required]);
    this.fg.controls.valueDateDays.addValidators([
      Validators.required,
      Validators.min(1),
    ]);
    this.fg.controls.valueDecimal.addValidators([Validators.required]);
    this.fg.controls.valueDecimalFrom.addValidators([Validators.required]);
    this.fg.controls.valueDecimalTo.addValidators([Validators.required]);
    this.fg.controls.operatorInteger.addValidators([Validators.required]);
    this.fg.controls.valueInteger.addValidators([Validators.required]);
    this.fg.controls.valueIntegerFrom.addValidators([Validators.required]);
    this.fg.controls.valueIntegerTo.addValidators([Validators.required]);
    this.fg.controls.valueBoolean.addValidators([Validators.required]);
    this.fg.controls.valueNotifyDays.setValue(1);
    this.disableAllValueControls();
  }

  clientFields: AlertMiscFieldOption[] = [];

  fieldValues: Record<number, AlertMiscFieldValue[]> = {};

  optionsNoValueDescription: any[] = [
    { label: 'Equal to', value: 'equalTo' },
    { label: 'Not equal to', value: 'notEqualTo' },
    { label: 'Is null', value: 'isNull' },
    { label: 'Is not null', value: 'isNotNull' },
    { label: 'Like', value: 'like' },
  ];

  optionsList: any[] = [
    { label: 'In list', value: 'inList' },
    { label: 'Not in list', value: 'notInList' },
  ];

  optionsInteger: any[] = [
    { label: '', value: null },
    { label: 'Equal to', value: 'equalTo' },
    { label: 'Not equal to', value: 'notEqualTo' },
    { label: 'Greater than or equal to', value: 'greaterThanOrEqualTo' },
    { label: 'Less than or equal to', value: 'lessThanOrEqualTo' },
    { label: 'Between', value: 'between' },
  ];

  booleanOptions: any[] = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ];

  notifyOptions: any[] = [
    { label: '1 day', value: 1 },
    { label: '5 days', value: 5 },
    { label: '10 days', value: 10 },
  ];

  getFieldType(id: number) {
    const field = this.findField(id);
    return field?.dataType ?? null;
  }

  getMaxLength(id: number) {
    const field = this.findField(id);
    if (!field?.format) {
      return 0;
    }

    const match = field.format.match(/\((\d+)\)/);
    return match ? parseInt(match[1], 10) : 0;
  }

  getValueList(id: number) {
    return this.fieldValues[id] ?? [];
  }

  private async loadFields(contractNumber: number | null, lobs: string[]) {
    if (!contractNumber) {
      this.clientFields = [];
      return;
    }

    const fields = await this.alerts.loadMiscFields(contractNumber, lobs);
    this.clientFields = fields.map((field) => {
      const name = (field.fieldName ?? '').trim();
      const description = name
        ? `${field.fieldNumber} - ${name}`
        : String(field.fieldNumber);
      return {
        value: field.fieldNumber,
        description,
        dataType: field.dataType,
        hasLookup: field.hasLookup,
        format: field.format,
      };
    });

    const selectedField = this.fg.controls.fieldName.value;
    if (selectedField && !this.findField(selectedField)) {
      this.fg.controls.fieldName.setValue(null);
    }
  }

  private async onFieldChanged(fieldNumber: number) {
    this.disableAllValueControls();
    if (!fieldNumber) {
      this.updateControl();
      return;
    }

    const field = this.findField(fieldNumber);
    if (!field) {
      this.updateControl();
      return;
    }

    if (field.hasLookup) {
      const alert = this.alerts.currentAlert();
      const contractNumber = alert?.contracts?.[0]?.contNum ?? null;
      if (contractNumber) {
        const values = await this.alerts.loadMiscFieldValues(
          contractNumber,
          fieldNumber,
        );
        this.fieldValues[fieldNumber] = values;
      } else {
        this.fieldValues[fieldNumber] = [];
      }
    } else {
      this.fieldValues[fieldNumber] = [];
    }

    const fieldType = field.dataType;
    if (fieldType === 'character') {
      if (this.getValueList(fieldNumber).length > 0) {
        this.fg.controls.operatorList.enable();
        this.fg.controls.operatorList.setValue(
          this.fg.controls.operatorList.value ?? 'inList',
        );
        this.fg.controls.valueSelectedCharacter.enable();
      } else {
        this.fg.controls.operatorNoDescription.enable();
        this.fg.controls.operatorNoDescription.setValue(
          this.fg.controls.operatorNoDescription.value ?? 'like',
          { emitEvent: false },
        );
        this.updateCharacterValueState(
          this.fg.controls.operatorNoDescription.value,
        );
      }
    } else if (fieldType === 'date') {
      this.fg.controls.valueDateDays.enable();
      this.fg.controls.valueDateDays.setValue(
        this.fg.controls.valueDateDays.value ?? 30,
      );
      this.fg.controls.valueNotifyDays.enable();
      this.fg.controls.valueNotifyDays.setValue(
        this.fg.controls.valueNotifyDays.value ?? 1,
      );
    } else if (fieldType === 'logical') {
      this.fg.controls.valueBoolean.enable();
      if (this.fg.controls.valueBoolean.value == null) {
        this.fg.controls.valueBoolean.setValue(true);
      }
    } else if (fieldType === 'integer' || fieldType === 'decimal') {
      this.fg.controls.operatorInteger.enable();
      this.updateNumericValidators(this.fg.controls.operatorInteger.value);
    }

    this.updateControl();
  }

  private applyControl(value: AlertCustomField) {
    this.fg.controls.fieldName.setValue(value.fieldNumber);
    this.fg.controls.operatorList.setValue(value.operator || null);
    this.fg.controls.operatorNoDescription.setValue(value.operator || null);
    this.fg.controls.operatorInteger.setValue(value.operator || null);
    this.fg.controls.valueCharacter.setValue(value.valueText || null);
    this.fg.controls.valueSelectedCharacter.setValue(value.values || null);
    this.fg.controls.valueDateDays.setValue(value.days ?? 30);
    this.fg.controls.valueNotifyDays.setValue(value.notifyDays ?? 1);
    this.fg.controls.valueInteger.setValue(value.valueFrom ?? null);
    this.fg.controls.valueDecimal.setValue(value.valueFrom ?? null);
    this.fg.controls.valueIntegerFrom.setValue(value.valueFrom ?? null);
    this.fg.controls.valueIntegerTo.setValue(value.valueTo ?? null);
    this.fg.controls.valueDecimalFrom.setValue(value.valueFrom ?? null);
    this.fg.controls.valueDecimalTo.setValue(value.valueTo ?? null);
    this.fg.controls.valueBoolean.setValue(value.booleanValue ?? null);
  }

  private updateControl() {
    if (!this.control) {
      return;
    }

    const field = this.findField(this.fg.controls.fieldName.value);
    if (!field) {
      this.control.setValue(null);
      return;
    }

    const fieldType = field.dataType;
    const customField: AlertCustomField = {
      fieldNumber: field.value,
      fieldName: field.description,
      dataType: fieldType,
    };

    if (fieldType === 'character') {
      if (this.getValueList(field.value).length > 0) {
        customField.operator = this.fg.controls.operatorList.value;
        customField.values =
          this.fg.controls.valueSelectedCharacter.value || [];
      } else {
        customField.operator = this.fg.controls.operatorNoDescription.value;
        customField.valueText =
          customField.operator === 'isNull' ||
          customField.operator === 'isNotNull'
            ? null
            : this.fg.controls.valueCharacter.value;
      }
    } else if (fieldType === 'date') {
      customField.operator = 'greaterThanOrEqualTo';
      customField.days = this.fg.controls.valueDateDays.value;
      customField.notifyDays = this.fg.controls.valueNotifyDays.value;
    } else if (fieldType === 'logical') {
      customField.operator = 'equals';
      customField.booleanValue = this.fg.controls.valueBoolean.value;
    } else if (fieldType === 'integer') {
      customField.operator = this.fg.controls.operatorInteger.value;
      if (customField.operator === 'between') {
        customField.valueFrom = this.fg.controls.valueIntegerFrom.value;
        customField.valueTo = this.fg.controls.valueIntegerTo.value;
      } else {
        customField.valueFrom = this.fg.controls.valueInteger.value;
      }
    } else if (fieldType === 'decimal') {
      customField.operator = this.fg.controls.operatorInteger.value;
      if (customField.operator === 'between') {
        customField.valueFrom = this.fg.controls.valueDecimalFrom.value;
        customField.valueTo = this.fg.controls.valueDecimalTo.value;
      } else {
        customField.valueFrom = this.fg.controls.valueDecimal.value;
      }
    }

    if (!this.customFieldValid(customField)) {
      this.control.setValue(null);
      return;
    }

    this.control.setValue(customField);
  }

  private customFieldValid(customField: AlertCustomField | null): boolean {
    if (customField?.fieldName == null) {
      return false;
    }

    switch (customField?.dataType) {
      case 'character':
        if (
          customField.operator === 'isNull' ||
          customField.operator === 'isNotNull'
        ) {
          return true;
        }
        if (
          customField.operator === 'inList' ||
          customField.operator === 'notInList'
        ) {
          return (customField.values?.length ?? 0) > 0;
        }
        return !!customField.operator && !!customField.valueText?.trim();
      case 'date':
        return (
          (customField.days ?? 0) >= 1 && (customField.notifyDays ?? 0) >= 1
        );
      case 'logical':
        return customField.booleanValue != null;
      case 'integer':
      case 'decimal':
        if (!customField.operator) {
          return false;
        }
        if (customField.operator === 'between') {
          return customField.valueFrom != null && customField.valueTo != null;
        }
        return customField.valueFrom != null;
      default:
        return false;
    }
  }

  private updateNumericValidators(operator: string) {
    if (!operator) {
      return;
    }

    const isBetween = operator === 'between';
    if (isBetween) {
      this.fg.controls.valueInteger.disable();
      this.fg.controls.valueDecimal.disable();
      this.fg.controls.valueIntegerFrom.enable();
      this.fg.controls.valueIntegerTo.enable();
      this.fg.controls.valueDecimalFrom.enable();
      this.fg.controls.valueDecimalTo.enable();
    } else {
      this.fg.controls.valueInteger.enable();
      this.fg.controls.valueDecimal.enable();
      this.fg.controls.valueIntegerFrom.disable();
      this.fg.controls.valueIntegerTo.disable();
      this.fg.controls.valueDecimalFrom.disable();
      this.fg.controls.valueDecimalTo.disable();
      this.fg.controls.valueIntegerFrom.setValue(null);
      this.fg.controls.valueIntegerTo.setValue(null);
      this.fg.controls.valueDecimalFrom.setValue(null);
      this.fg.controls.valueDecimalTo.setValue(null);
    }
  }

  private updateCharacterValueState(operator: string) {
    if (!operator) {
      return;
    }

    const isNullOp = operator === 'isNull' || operator === 'isNotNull';
    if (isNullOp) {
      this.fg.controls.valueCharacter.disable();
      this.fg.controls.valueCharacter.setValue(null);
    } else {
      this.fg.controls.valueCharacter.enable();
    }
  }

  private disableAllValueControls() {
    this.fg.controls.operatorList.disable();
    this.fg.controls.operatorNoDescription.disable();
    this.fg.controls.operatorInteger.disable();
    this.fg.controls.valueDateDays.disable();
    this.fg.controls.valueNotifyDays.disable();
    this.fg.controls.valueInteger.disable();
    this.fg.controls.valueDecimal.disable();
    this.fg.controls.valueBoolean.disable();
    this.fg.controls.valueSelectedCharacter.disable();
    this.fg.controls.valueCharacter.disable();
    this.fg.controls.valueIntegerFrom.disable();
    this.fg.controls.valueIntegerTo.disable();
    this.fg.controls.valueDecimalFrom.disable();
    this.fg.controls.valueDecimalTo.disable();
  }

  private findField(fieldNumber: number): AlertMiscFieldOption | undefined {
    return this.clientFields.find((field) => field.value === fieldNumber);
  }
}
